import type {
  EstimationItemCostResponse,
  EstimationItemResourceResponse,
  EstimationItemResponse,
  EstimationModelResponse,
  MethodologyResponse,
} from '@/api/schema/data-contracts';
import {
  ApiEstimationItemPurpose,
  ApiEstimationItemStatus,
  ApiIsCalculatedMode,
} from '@/api/schema/data-contracts';
import type { EstimationWithDependencies } from '@/hooks/useGetEstimationWithDependencies';
import { sortExpressions } from '@/utils/sorting';

import type {
  CostEstimationItemsTableRecord,
  EstimationItemsTableRecordCommon,
  EstimationItemTableRecordParameter,
  ResourceEstimationItemsTableRecord,
} from '../model';

type EstimationModelItem = EstimationModelResponse['items'][number];
type EstimationModelItemParameter = EstimationModelItem['parameters'][number];

/**
 * Precomputed lookups over a single estimation's data.
 *
 * Building the table records naively re-scans the full `estimationItems`,
 * `assumptions`, `comments`, `estimationItemCosts` and `resources` arrays once
 * per item (and the flattened estimation-model parameter list once per
 * parameter), which is O(N²)–O(N³) in the estimation size. On large estimations
 * that blocked the browser main thread long enough to trip the "page not
 * responding" dialog (EWM-861, LBBW PROD). This index is built once per data
 * load so each per-item lookup below is O(1).
 */
export interface EstimationItemsRecordIndex {
  /** All child items grouped by parent id (undefined key = top-level), each bucket sorted by order. */
  childrenByParentId: Map<string | undefined, EstimationItemResponse[]>;
  /** Hierarchical order reference string (e.g. "1.2.3") per item id. */
  referenceByItemId: Map<string, string>;
  assumptionCountByItemId: Map<string, number>;
  commentCountByItemId: Map<string, number>;
  itemCostsByItemId: Map<string, EstimationItemCostResponse[]>;
  /** undefined when resources were not fetched, matching the prior `resources?.filter` behaviour. */
  resourcesByItemId: Map<string, EstimationItemResourceResponse[]> | undefined;
  /** Item ids that have at least one estimation-item cost with a non-null price. */
  itemIdsWithFilledCost: Set<string>;
  /** Item ids that have at least one resource with a non-null value. */
  itemIdsWithFilledResource: Set<string>;
  modelItemsById: Map<string, EstimationModelItem>;
  modelItemParametersById: Map<string, EstimationModelItemParameter>;
  methodologiesById: Map<string, MethodologyResponse>;
  /** Estimation-level methodology id, used as the per-item fallback. */
  estimationMethodologyId: string | undefined;
}

const buildReferenceByItemId = (estimationItems: EstimationItemResponse[]): Map<string, string> => {
  const itemsById = new Map(estimationItems.map((item) => [item.id, item] as const));
  const referenceByItemId = new Map<string, string>();

  const buildReference = (item: EstimationItemResponse): string => {
    const cached = referenceByItemId.get(item.id);
    if (cached !== undefined) {
      return cached;
    }
    const parent = item.parentId ? itemsById.get(item.parentId) : undefined;
    const reference = parent ? `${buildReference(parent)}.${item.order}` : item.order.toString();
    referenceByItemId.set(item.id, reference);
    return reference;
  };

  estimationItems.forEach(buildReference);
  return referenceByItemId;
};

export const buildEstimationItemsRecordIndex = (
  data: EstimationWithDependencies,
): EstimationItemsRecordIndex => {
  const { estimationItems, estimationModel, methodologies, assumptions, comments } = data;

  const childrenByParentId = new Map<string | undefined, EstimationItemResponse[]>();
  for (const item of estimationItems) {
    const parentKey = item.parentId ?? undefined;
    const bucket = childrenByParentId.get(parentKey);
    if (bucket) {
      bucket.push(item);
    } else {
      childrenByParentId.set(parentKey, [item]);
    }
  }
  for (const bucket of childrenByParentId.values()) {
    bucket.sort(sortExpressions.byOrder);
  }

  const assumptionCountByItemId = new Map<string, number>();
  for (const assumption of assumptions) {
    assumptionCountByItemId.set(
      assumption.estimationItemId,
      (assumptionCountByItemId.get(assumption.estimationItemId) ?? 0) + 1,
    );
  }

  const commentCountByItemId = new Map<string, number>();
  for (const comment of comments) {
    if (!comment.estimationItemId) {
      continue;
    }
    commentCountByItemId.set(
      comment.estimationItemId,
      (commentCountByItemId.get(comment.estimationItemId) ?? 0) + 1,
    );
  }

  const itemCostsByItemId = new Map<string, EstimationItemCostResponse[]>();
  const itemIdsWithFilledCost = new Set<string>();
  for (const cost of data.estimationItemCosts ?? []) {
    const bucket = itemCostsByItemId.get(cost.estimationItemId);
    if (bucket) {
      bucket.push(cost);
    } else {
      itemCostsByItemId.set(cost.estimationItemId, [cost]);
    }
    if (cost.price !== null) {
      itemIdsWithFilledCost.add(cost.estimationItemId);
    }
  }

  const resourcesByItemId =
    data.resources === undefined ? undefined : new Map<string, EstimationItemResourceResponse[]>();
  const itemIdsWithFilledResource = new Set<string>();
  if (resourcesByItemId) {
    for (const resource of data.resources ?? []) {
      const bucket = resourcesByItemId.get(resource.estimationItemId);
      if (bucket) {
        bucket.push(resource);
      } else {
        resourcesByItemId.set(resource.estimationItemId, [resource]);
      }
      if (resource.value !== null) {
        itemIdsWithFilledResource.add(resource.estimationItemId);
      }
    }
  }

  const modelItemsById = new Map<string, EstimationModelItem>();
  const modelItemParametersById = new Map<string, EstimationModelItemParameter>();
  for (const modelItem of estimationModel.items) {
    modelItemsById.set(modelItem.id, modelItem);
    for (const parameter of modelItem.parameters) {
      modelItemParametersById.set(parameter.id, parameter);
    }
  }

  const methodologiesById = new Map(
    methodologies.map((methodology) => [methodology.id, methodology] as const),
  );

  return {
    childrenByParentId,
    referenceByItemId: buildReferenceByItemId(estimationItems),
    assumptionCountByItemId,
    commentCountByItemId,
    itemCostsByItemId,
    resourcesByItemId,
    itemIdsWithFilledCost,
    itemIdsWithFilledResource,
    modelItemsById,
    modelItemParametersById,
    methodologiesById,
    estimationMethodologyId: data.estimation.methodologyId ?? undefined,
  };
};

const getEstimationItemsTableRecordCommon = (
  estimationItem: EstimationItemResponse,
  index: EstimationItemsRecordIndex,
): EstimationItemsTableRecordCommon => {
  const estimationModelItem = estimationItem.estimationModelItemId
    ? index.modelItemsById.get(estimationItem.estimationModelItemId)
    : undefined;

  return {
    id: estimationItem.id,
    reference: index.referenceByItemId.get(estimationItem.id) ?? estimationItem.order.toString(),
    order: estimationItem.order,
    name: estimationItem.name,
    description: estimationItem.description ?? undefined,
    parentId: estimationItem.parentId ?? undefined,
    estimationModelGroupId: estimationItem.estimationModelGroupId,
    isOmitted: estimationItem.status === ApiEstimationItemStatus.OMITTED,
    omitReason: estimationItem.omitReason ?? undefined,
    isParentBasedCalculation: estimationItem.isParentBasedCalculation,
    assumptionsCount: index.assumptionCountByItemId.get(estimationItem.id) ?? 0,
    commentsCount: index.commentCountByItemId.get(estimationItem.id) ?? 0,
    isRequired: estimationModelItem?.isRequired ?? false,
    estimationModelItemId: estimationModelItem?.id,
  };
};

export const getCostEstimationItemsTableRecord = (
  estimationItem: EstimationItemResponse,
  index: EstimationItemsRecordIndex,
): CostEstimationItemsTableRecord => {
  const children = index.childrenByParentId.get(estimationItem.id) ?? [];
  return {
    ...getEstimationItemsTableRecordCommon(estimationItem, index),
    estimationItemCosts: index.itemCostsByItemId.get(estimationItem.id) ?? [],
    areFilledSubItemsEstimationItemCosts: children.some((child) =>
      index.itemIdsWithFilledCost.has(child.id),
    ),
  };
};

export const getResourceEstimationItemsTableRecord = (
  estimationItem: EstimationItemResponse,
  index: EstimationItemsRecordIndex,
): ResourceEstimationItemsTableRecord => {
  const estimationModelItem = estimationItem.estimationModelItemId
    ? index.modelItemsById.get(estimationItem.estimationModelItemId)
    : undefined;
  const methodologyIdWithFallback = estimationItem.parameters.find(
    (x) => !!x.methodologyParameterId,
  )
    ? (estimationItem.methodologyId ?? index.estimationMethodologyId)
    : undefined;
  const methodologyWithFallback = methodologyIdWithFallback
    ? index.methodologiesById.get(methodologyIdWithFallback)
    : undefined;

  const parameters = estimationItem.parameters
    .sort(sortExpressions.byOrder)
    .map((estimationItemParameter) => {
      const estimationModelItemParameter = estimationItemParameter.estimationModelItemParameterId
        ? index.modelItemParametersById.get(estimationItemParameter.estimationModelItemParameterId)
        : undefined;
      return {
        id: estimationItemParameter.id,
        order: estimationItemParameter.order,
        parameterName: estimationModelItemParameter
          ? estimationModelItemParameter.label
          : methodologyWithFallback?.defaultParameters.find(
              (methodologyParameter) =>
                methodologyParameter.id === estimationItemParameter.methodologyParameterId,
            )?.label,
        value: estimationItemParameter.value ?? undefined,
        defaultValue: estimationModelItemParameter?.defaultValue,
        methodologyParameterId: estimationItemParameter.methodologyParameterId,
        estimationModelItemParameterId: estimationItemParameter.estimationModelItemParameterId,
      } as EstimationItemTableRecordParameter;
    });

  const estimationModelItemParameters = estimationModelItem?.parameters.map((parameter) => ({
    id: parameter.id,
    defaultValue: parameter.defaultValue ?? undefined,
  }));

  const children = index.childrenByParentId.get(estimationItem.id) ?? [];

  return {
    ...getEstimationItemsTableRecordCommon(estimationItem, index),
    hasEstimationModelItemParameters:
      !!estimationModelItem && estimationModelItem.parameters.length > 0,
    isCalculated: estimationModelItem?.isCalculated ?? ApiIsCalculatedMode.NONE,
    methodology: methodologyWithFallback
      ? {
          id: methodologyWithFallback.id,
          name: methodologyWithFallback.name,
          order: methodologyWithFallback.order,
        }
      : undefined,
    calculatedResult: estimationItem.calculatedResult ?? undefined,
    childrenCount: children.length,
    phaseLeadId: estimationItem.phaseLeadId ?? undefined,
    params: parameters,
    estimationModelItemParameters,
    resources:
      index.resourcesByItemId === undefined
        ? undefined
        : (index.resourcesByItemId.get(estimationItem.id) ?? []),
    areFilledSubItemsResources: children.some((child) =>
      index.itemIdsWithFilledResource.has(child.id),
    ),
    // Filled in by buildResourceRecordsByParentId once the whole tree exists.
    subItemsParamValueSums: new Map<string, number>(),
  };
};

/**
 * A phase estimated through its sub-items keeps its own parameter fields empty - the values live
 * on the children - so the parameter columns rendered nothing at all for it and could not be
 * totalled (EWM-987). Roll the descendants' values up per parameter name, mirroring what the table
 * displays for each child: a child that is itself sub-item-based contributes its own rolled-up sum.
 */
const fillSubItemsParamValueSums = (
  record: ResourceEstimationItemsTableRecord,
  recordsByParentId: Map<string | undefined, ResourceEstimationItemsTableRecord[]>,
): Map<string, number> => {
  for (const child of recordsByParentId.get(record.id) ?? []) {
    const childSums = fillSubItemsParamValueSums(child, recordsByParentId);
    if (child.isOmitted) {
      continue;
    }
    const isChildSubItemBased = !child.isParentBasedCalculation && child.childrenCount > 0;
    for (const parameter of child.params) {
      if (!parameter.parameterName) {
        continue;
      }
      const value = isChildSubItemBased
        ? (childSums.get(parameter.parameterName) ?? 0)
        : (parameter.value ?? 0);
      record.subItemsParamValueSums.set(
        parameter.parameterName,
        (record.subItemsParamValueSums.get(parameter.parameterName) ?? 0) + value,
      );
    }
  }
  return record.subItemsParamValueSums;
};

/**
 * Builds every resource table record once, grouped by parent id (undefined =
 * top level). Records within a parent are ordered by `order`. Empty parents are
 * omitted; callers should treat a missing key as an empty list.
 */
export const buildResourceRecordsByParentId = (
  index: EstimationItemsRecordIndex,
): Map<string | undefined, ResourceEstimationItemsTableRecord[]> => {
  const recordsByParentId = new Map<string | undefined, ResourceEstimationItemsTableRecord[]>();
  for (const [parentId, children] of index.childrenByParentId) {
    const records = children
      .filter((item) => item.purpose === ApiEstimationItemPurpose.RESOURCE)
      .map((item) => getResourceEstimationItemsTableRecord(item, index));
    if (records.length > 0) {
      recordsByParentId.set(parentId, records);
    }
  }
  for (const record of recordsByParentId.get(undefined) ?? []) {
    fillSubItemsParamValueSums(record, recordsByParentId);
  }
  return recordsByParentId;
};

/**
 * Builds every cost table record once, grouped by parent id (undefined = top
 * level). See {@link buildResourceRecordsByParentId}.
 */
export const buildCostRecordsByParentId = (
  index: EstimationItemsRecordIndex,
): Map<string | undefined, CostEstimationItemsTableRecord[]> => {
  const recordsByParentId = new Map<string | undefined, CostEstimationItemsTableRecord[]>();
  for (const [parentId, children] of index.childrenByParentId) {
    const records = children
      .filter((item) => item.purpose === ApiEstimationItemPurpose.COST)
      .map((item) => getCostEstimationItemsTableRecord(item, index));
    if (records.length > 0) {
      recordsByParentId.set(parentId, records);
    }
  }
  return recordsByParentId;
};
